import { Card, Frame, Inline } from 'almanac-ui/icons'
import { ReactComponent as CardIcon } from 'assets/icons/card.svg'
import { ReactComponent as CommentIcon } from 'assets/icons/comment24.svg'
import { ReactComponent as TrashIcon } from 'assets/icons/delete24.svg'
import { ReactComponent as FrameIcon } from 'assets/icons/frame.svg'
import { ReactComponent as InlineIcon } from 'assets/icons/inline.svg'
import cx from 'classnames'
import { useReactNodeView } from 'common/Prose/nodeViews/ReactNodeView/ReactNodeViewContext'
import { embedModeToIframeFormat, iframeFormatToEmbedMode } from 'common/Prose/utils/iframeFormatToEmbedModeConverter'
import { Tooltip } from 'common/Tooltip/Tooltip'
import { wrapURLWithHandbook } from 'components/Handbook2_0/util'
import { InlineFormatBar } from 'components/InlineFormatBar/InlineFormatBar'
import { InlineFormatButton } from 'components/InlineFormatBar/components/InlineFormatButton/InlineFormatButton'
import { InlineFormatDropdownMenuItem } from 'components/InlineFormatBar/components/InlineFormatDropdownMenuItem/InlineFormatDropdownMenuItem'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { DeleteInlineFormatButton } from '../TextInlineFormatBar/components/Buttons/DeleteInlineFormatButton'
import { TurnIntoLinkButton } from '../TextInlineFormatBar/components/Buttons/TurnIntoLinkButton'
import { ProseDropdownPrompt } from '../TextInlineFormatBar/components/Buttons/ProseDropdownPrompt/ProseDropdownPrompt'
import styles from './FormatBar.module.scss'
import { Option, Select } from './Select/Select'

type Mode = 'inline' | 'card' | 'frame'

export type FormatBarProps = {
  icon:
    | React.FunctionComponent<React.PropsWithChildren<React.SVGProps<SVGSVGElement>>>
    | string
    | React.ForwardRefExoticComponent<any>
  title: string
  url: string
  mode: Mode
  subtitle?: string
  containerRef?: React.RefObject<HTMLDivElement>
}

export const FormatBar: React.FC<React.PropsWithChildren<FormatBarProps>> = (props) => {
  const { icon: Icon, title, subtitle, url } = props
  const { node, view, getPos } = useReactNodeView()
  const mode = node?.attrs.mode || iframeFormatToEmbedMode(node?.attrs.format)
  const editable = view?.editable ?? false
  const newFormatBar = useFlipperFlag('new_inline_format_bar')

  const setMode = (mode: FormatBarProps['mode']) => () => {
    const {
      dispatch,
      state: { tr },
    } = // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      view!

    if (node?.attrs.mode) {
      if (node?.attrs.mode === mode) return

      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dispatch(tr.setNodeMarkup((getPos as () => number)(), undefined, { ...node!.attrs, mode }))
    }

    const iframeFormat = embedModeToIframeFormat(mode)
    if (node?.attrs.format) {
      if (node?.attrs.format === iframeFormat) return

      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dispatch(tr.setNodeMarkup((getPos as () => number)(), undefined, { ...node!.attrs, format: iframeFormat }))
    }
  }

  const handleDelete = () => {
    if (!editable) return
    const {
      dispatch,
      state: { tr },
    } = // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      view!

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    dispatch(tr.delete((getPos as () => number)(), (getPos as () => number)() + node!.nodeSize))
  }

  const handleVisit = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    const handbookURL = wrapURLWithHandbook(e.currentTarget.href)

    if (handbookURL) {
      e.preventDefault()
      window.open(handbookURL, '_blank')?.focus()
    }
  }

  // New Format Bar

  const conversionOptions: Array<{ key: Mode; icon: ReactElement; label: string }> = [
    {
      key: 'inline',
      icon: <Inline />,
      label: 'Inline',
    },
    {
      key: 'card',
      icon: <Card />,
      label: 'Card',
    },
    {
      key: 'frame',
      icon: <Frame />,
      label: 'Frame',
    },
  ]

  const activeModeButton = useMemo(() => {
    const active = conversionOptions.filter(({ key }) => key === mode)[0]

    return (
      <>
        {active.icon}
        {active.label}
      </>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  if (newFormatBar) {
    return (
      <div className={styles.container} data-testid="embedFormatBar">
        <InlineFormatBar className={styles.embedFormatBar}>
          <ProseDropdownPrompt
            withChevron
            placement="bottom-start"
            view={view}
            anchorChildren={activeModeButton}
            label={'Change embed display type'}
            isDisabled={!editable}
            dropdownComponent={(close) => (
              <div className={styles.conversionDropdown}>
                {conversionOptions.map(({ key, icon, label }) => (
                  <InlineFormatDropdownMenuItem
                    key={key}
                    isDisabled={!editable}
                    onClick={() => {
                      setMode(key)()
                      close()
                    }}
                    aria-label={label}
                    label={label}
                    icon={icon}
                    active={key === mode}
                  />
                ))}
              </div>
            )}
          />
          <InlineFormatButton className={styles.embedLink} data-testid="embed-link">
            <a target="_blank" rel="noopener noreferrer nofollow ugc" href={url}>
              {typeof Icon === 'string' ? <img width={16} height={16} src={Icon} alt="Favicon" /> : <Icon />}
              <span className={styles.linkTitle}>{url}</span>
            </a>
          </InlineFormatButton>
          <TurnIntoLinkButton view={view} />
          <DeleteInlineFormatButton view={view} />
        </InlineFormatBar>
      </div>
    )
  }

  return (
    <div className={styles.FormatBar}>
      <div className={styles.bar}>
        <div className={styles.meta}>
          {typeof Icon === 'string' ? (
            <img width={32} height={32} className={styles.icon} src={Icon} alt="Favicon" />
          ) : (
            <Icon width={32} height={32} className={styles.icon} />
          )}

          <a
            className={cx(styles.title, { [styles.noSubTitle]: !subtitle })}
            href={url}
            target="_blank"
            rel="noopener noreferrer ugc"
            onClick={handleVisit}
          >
            {title}
          </a>
          {subtitle && (
            <a
              className={styles.subTitle}
              href={url}
              target="_blank"
              rel="noopener noreferrer ugc"
              onClick={handleVisit}
            >
              {subtitle}
            </a>
          )}
        </div>

        <div className={styles.operations}>
          <Select className={styles.select} selectDisabled={!editable}>
            <Option className={styles.option} selected={mode === 'inline'} onSelect={setMode('inline')}>
              <InlineIcon width={24} height={24} />
              Inline
            </Option>
            <Option className={styles.option} selected={mode === 'card'} onSelect={setMode('card')}>
              <CardIcon width={24} height={24} />
              Card
            </Option>
            <Option className={styles.option} selected={mode === 'frame'} onSelect={setMode('frame')}>
              <FrameIcon width={24} height={24} />
              Frame
            </Option>
          </Select>

          <div className={cx(styles.btn, styles.disabled)}>
            <Tooltip label="Coming soon" placement="bottom">
              <CommentIcon className={styles.btnIcon} />
            </Tooltip>
          </div>

          <div className={cx(styles.btn, { [styles.disabled]: !editable })} data-test-id="formatBarDelete">
            <TrashIcon className={styles.btnIcon} onClick={handleDelete} />
          </div>
        </div>
      </div>
    </div>
  )
}
